import type {
  GetCredentialsResponse,
  UpdateMerchantEcommerceConnector,
} from '../../../store/modules/merchant/types';
import { findDefaultCredential } from '../../../util/credentials';
import { buildEcommerceConnectorData } from '../../../util/ecommerceConnector';
import { normalizeSquarespaceExternalId } from '../../../util/squarespaceExternalId';
import {
  findSquarespaceCredential,
  getSquarespaceConnectionStatus,
  getSquarespaceSite,
} from '../provisioning/squarespace/squarespaceProvisioning';

/** Platforms whose store link the wizard may replace with a Squarespace site. */
const RELINKABLE_SYSTEMS_OF_ORIGIN = new Set(['custom', 'squarespace']);

/**
 * The merchant's Squarespace link. Editable until the first connect; after it, relinking would
 * orphan the live connection.
 */
export type SquarespaceStoreLink = {
  /** Connected (or expired), so it can no longer change. */
  isLocked: boolean;
  linkedHost?: string;
  /** Only set while unconnected. */
  linkedSubdomain?: string;
  merchantExternalPlatformId?: string | null;
  /** False when another platform owns the merchant's store link. */
  canRelink: boolean;
};

export const getSquarespaceStoreLink = (
  credentials: GetCredentialsResponse | undefined,
  merchantExternalPlatformId: string | null | undefined,
): SquarespaceStoreLink => {
  const credential = findSquarespaceCredential(credentials);
  const isLocked = getSquarespaceConnectionStatus(credential) !== 'not_connected';
  const site = getSquarespaceSite(credential);
  const linkedSubdomain = isLocked
    ? undefined
    : (normalizeSquarespaceExternalId(site?.host ?? '') ?? undefined);

  const defaultCredential = findDefaultCredential(credentials, 'ecommerce');
  const canRelink =
    !merchantExternalPlatformId ||
    RELINKABLE_SYSTEMS_OF_ORIGIN.has(defaultCredential?.systemOfOrigin ?? '');

  return {
    isLocked,
    linkedHost: site?.host,
    linkedSubdomain,
    merchantExternalPlatformId,
    canRelink,
  };
};

export const SQUARESPACE_SITE_REQUIRED_ERROR = 'Squarespace site is required';
export const SQUARESPACE_SITE_FORMAT_ERROR =
  'Enter the built-in *.squarespace.com address or subdomain, not a custom domain';
export const SQUARESPACE_SITE_LINKED_ELSEWHERE_ERROR =
  'This merchant is already linked to a different store.';

/** Without a link, only the format is checked. */
export const getSquarespaceSiteError = (
  squarespaceSite: string,
  link?: SquarespaceStoreLink,
): string | undefined => {
  if (link?.isLocked) {
    return undefined;
  }

  const subdomain = normalizeSquarespaceExternalId(squarespaceSite);
  if (!subdomain) {
    return squarespaceSite.trim() ? SQUARESPACE_SITE_FORMAT_ERROR : SQUARESPACE_SITE_REQUIRED_ERROR;
  }

  if (
    link &&
    !link.canRelink &&
    link.merchantExternalPlatformId &&
    link.merchantExternalPlatformId !== subdomain
  ) {
    return SQUARESPACE_SITE_LINKED_ELSEWHERE_ERROR;
  }

  return undefined;
};

/** The connector to save on publish, or null when already linked to this site. */
export const buildSquarespaceStoreLinkUpdate = (
  squarespaceSite: string,
  link: SquarespaceStoreLink,
): UpdateMerchantEcommerceConnector | null => {
  if (link.isLocked) {
    return null;
  }

  const subdomain = normalizeSquarespaceExternalId(squarespaceSite);
  if (!subdomain) {
    return null;
  }

  if (link.linkedSubdomain === subdomain && link.merchantExternalPlatformId === subdomain) {
    return null;
  }

  return { type: 'squarespace', ...buildEcommerceConnectorData('squarespace', subdomain) };
};
